import { Box, Center, Skeleton } from '@chakra-ui/react';
import type { SessionStoreInstance } from '@halo/chat-sdk-react';
import { useAppStore, WsStatus, type Client, type Msg } from '@halo/chat-sdk-react';
import { ImgIcon } from '@halo/ui-chat-react/assets/icons';
import { type FileMsgInfo } from '@halo/chat-sdk-react';
import { isEqual } from 'lodash-es';
import { type MsgFileStatusProps } from '../MessageFile';
import { MessageUpload } from '../MessageMeta/MessageUpload';

export interface MessageImageProps extends Pick<MsgFileStatusProps, 'onAbort'> {
  message: Msg;
  onImageClick: () => void;
  sessionStore?: SessionStoreInstance;
  client?: Client;
}

const LIMIT_WIDTH_MAX = 680;
const LIMIT_WIDTH_MIN = 200;
const LIMIT_HEIGHT_MAX = 320;
const LIMIT_HEIGHT_MIN = 94;
const LIMIT_RATE = 15 / 1;
// const LIMIT_MAX = 400;
// const LIMIT_MIN = 40;

export default function MessageImage({
  message,
  onImageClick,
  sessionStore,
  client,
}: MessageImageProps) {
  // TODO amo: Get sessionStore by props

  const [fileMsg, setFileMsg] = useState<FileMsgInfo>();

  useEffect(() => {
    const unsubscribe = sessionStore?.subscribe(
      (state) => state.fileMsgs[message.cli_msg_id],
      (fileMsg) => {
        setFileMsg(fileMsg);
      },
      {
        fireImmediately: true,
        equalityFn: (a, b) => isEqual(a, b),
      }
    );
    return unsubscribe;
  }, [message.cli_msg_id, sessionStore]);

  const thumbnail: string | undefined = useMemo(
    () => fileMsg?.file || message.payload?.thumbnail,
    [message.payload?.thumbnail, fileMsg]
  );

  // useEffect(() => {
  //   if (thumbnail?.startsWith('blob:')) {
  //     return;
  //   }

  //   let objUrl: string;

  //   new MsgFileCache(message.cli_msg_id).getFile().then((file) => {
  //     if (file) {
  //       objUrl = URL.createObjectURL(file);
  //     }
  //   });
  // }, [message.cli_msg_id, thumbnail, setThumbnail]);

  const imageSize = useMemo(() => {
    const width: number = message.payload.width;
    const height: number = message.payload.height;

    if (!width) return undefined;
    if (!height) return undefined;

    let [w, h] = [width, height];

    const imageRateWidth = width / height;
    const imageRateHeight = height / width;
    const isOverSize = imageRateWidth > LIMIT_RATE || imageRateHeight > LIMIT_RATE;
    // if oversize
    if (isOverSize) {
      // width over
      if (imageRateWidth > LIMIT_RATE) {
        // if (h < LIMIT_HEIGHT_MIN) h = LIMIT_HEIGHT_MIN;
        // if (h > LIMIT_HEIGHT_MAX) h = LIMIT_HEIGHT_MAX;
        h = LIMIT_HEIGHT_MIN;
        w = w * (h / height);
      }
      // height over
      if (imageRateHeight > LIMIT_RATE) {
        // if (w < LIMIT_WIDTH_MIN) w = LIMIT_WIDTH_MIN;
        // if (w > LIMIT_WIDTH_MAX) w = LIMIT_WIDTH_MAX;
        w = LIMIT_HEIGHT_MIN;
        h = h * (w / width);
      }
    }
    if (!isOverSize) {
      if (width >= height) {
        if (h < LIMIT_HEIGHT_MIN) h = LIMIT_HEIGHT_MIN;
        if (h > LIMIT_HEIGHT_MAX) h = LIMIT_HEIGHT_MAX;
        w = w * (h / height);
        if (w < LIMIT_WIDTH_MIN) w = LIMIT_WIDTH_MIN;
        if (w > LIMIT_WIDTH_MAX) w = LIMIT_WIDTH_MAX;
        h = height * (w / width);
      }
      if (width < height) {
        if (w < LIMIT_WIDTH_MIN) w = LIMIT_WIDTH_MIN;
        if (w > LIMIT_WIDTH_MAX) w = LIMIT_WIDTH_MAX;
        h = h * (w / width);
        if (h < LIMIT_HEIGHT_MIN) h = LIMIT_HEIGHT_MIN;
        if (h > LIMIT_HEIGHT_MAX) h = LIMIT_HEIGHT_MAX;
        w = width * (h / height);
      }
    }

    let boxW = w;
    if (boxW > LIMIT_WIDTH_MAX) boxW = LIMIT_WIDTH_MAX;
    // if (boxW < LIMIT_WIDTH_MIN) boxW = LIMIT_WIDTH_MIN;
    let boxH = h;
    if (boxH > LIMIT_HEIGHT_MAX) boxH = LIMIT_HEIGHT_MAX;
    // if (boxH < LIMIT_HEIGHT_MIN) boxH = LIMIT_HEIGHT_MIN;

    if (isOverSize) {
      if (imageRateWidth > LIMIT_RATE) {
        boxW = LIMIT_WIDTH_MIN;
        boxH = LIMIT_HEIGHT_MIN;
      }
      // height over
      if (imageRateHeight > LIMIT_RATE) {
        boxW = LIMIT_HEIGHT_MIN;
        boxH = LIMIT_WIDTH_MIN;
      }
    }

    return {
      width: w,
      height: h,
      boxWidth: boxW,
      boxHeight: boxH,
      long: isOverSize,
    };
  }, [message.payload.width, message.payload.height]);

  const loading = useMemo(() => !imageSize || !thumbnail, [imageSize, thumbnail]);

  // network reconnect check thumbnail is empty and try reload message
  const wsStatus = useAppStore((state) => state.wsStatus);
  useEffect(() => {
    if (wsStatus !== WsStatus.Open) return;
    if (!loading) return;
    if (!message.payload?.thumbnail_error) return;
    // reload message
    const session = sessionStore?.getState().session;
    if (!session?.chat_id) return;
    const promise = client?.Api.messages.getMsgByCliMsgId(session.chat_id, message.cli_msg_id);

    return promise?.removeListener;
  }, [
    wsStatus,
    message.payload?.thumbnail_error,
    loading,
    client,
    sessionStore,
    message.cli_msg_id,
  ]);

  return (
    <Box
      className="MessageImage"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width={loading ? '204px' : imageSize?.boxWidth + 'px'}
      height={loading ? '204px' : imageSize?.boxHeight + 'px'}
      position="relative"
      onClick={onImageClick}
      cursor="pointer"
      minW="10"
      minH="10"
      margin="auto"
      userSelect="none"
    >
      {loading ? (
        <Box position="relative" w="full" h="full">
          <Skeleton w="full" h="full" />
          <Center position="absolute" top="0" left="0" w="full" h="full" fontSize="40px">
            <ImgIcon />
          </Center>
        </Box>
      ) : (
        <>
          <img
            src={thumbnail}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: imageSize?.long ? 'cover' : undefined,
              objectPosition: 'left top',
              borderRadius: '10px',
            }}
          />
          {imageSize?.long && (
            <Box
              bg="blackAlpha.50"
              color="white"
              pos="absolute"
              left="1"
              top="1"
              fontSize="xs"
              px="1"
              py="0.5"
              borderRadius="sm"
              cursor="default"
            >
              Long
            </Box>
          )}
        </>
      )}
      {thumbnail && (
        <MessageUpload
          message={message}
          sessionStore={sessionStore}
          fileMsg={fileMsg}
          boxSize="9"
          fontSize="20px"
        />
      )}
    </Box>
  );
}

export { MessageImage };
